import WorkLog from "../models/WorkLog";
import OpsDateUtils from "./OpsDateUtils";

/**
 * A week of the worklog month, the way the weekly approval slices it: Sunday to Saturday, cut at the month edges.
 * The first week of a month that does not start on a Sunday starts on the 1st, and the last one ends on the last day of the month.
 */
export interface WorklogWeek {
  key: string;
  year: number;
  month: number;
  startDay: number;
  endDay: number;
  /** Same date the dashboard sends to the approval endpoint. */
  firstDay: Date;
  approved: boolean;
  hasActiveDays: boolean;
  label: string;
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const weekKey = (year: number, month: number, startDay: number): string => `${year}-${month}-${startDay}`;

/** First day of the (Sunday to Saturday) week of the given date, clamped to the 1st when the Sunday falls in the previous month. */
export const weekStartDayOf = (date: Date): number => Math.max(1, date.getDate() - date.getDay());

export const toWeekFirstDay = (year: number, month: number, startDay: number): Date => OpsDateUtils.toUTC(new Date(year, month, startDay));

const labelOf = (month: number, startDay: number, endDay: number): string =>
  startDay === endDay ? `${SHORT_MONTHS[month]} ${startDay}` : `${SHORT_MONTHS[month]} ${startDay}\u2013${endDay}`;

/** `overrides` wins over the listing until the reloaded rows agree. */
export const groupRowsByWeek = (
  rows: WorkLog[],
  isAssignationActive: (row: WorkLog) => boolean,
  overrides: Record<string, boolean> = {},
): WorklogWeek[] => {
  if (rows.length === 0) return [];

  const year = rows[0].shortDay.getFullYear();
  const month = rows[0].shortDay.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const weeks = new Map<number, { rows: WorkLog[]; endDay: number }>();

  rows.forEach((row) => {
    const startDay = weekStartDayOf(row.shortDay);
    const endDay = Math.min(lastDay, startDay + (6 - new Date(year, month, startDay).getDay()));
    const week = weeks.get(startDay) ?? { rows: [], endDay };
    week.rows.push(row);
    weeks.set(startDay, week);
  });

  return Array.from(weeks.entries())
    .sort(([a], [b]) => a - b)
    .map(([startDay, week]) => {
      const key = weekKey(year, month, startDay);
      return {
        key,
        year,
        month,
        startDay,
        endDay: week.endDay,
        firstDay: toWeekFirstDay(year, month, startDay),
        approved: key in overrides ? overrides[key] : week.rows.some((row) => row.weeklyApproved === true),
        hasActiveDays: week.rows.some(isAssignationActive),
        label: labelOf(month, startDay, week.endDay),
      };
    });
};
